// TanStack Query keys + fetchers for the /admin/ops/* surface.
// Mirrors the shape of queries.ts but lives in its own file so the
// Phase 4 surface is self-contained.

import { api, qs } from "./api";
import type {
  BrokerPositions,
  DashboardSummary,
  CaseState,
  Finding,
  FindingsPage,
  ProviderTrace,
  ProviderTraceQuery,
  RunDetail,
  RunsPage,
  Severity,
  TaskType,
  TradeIntegrity,
  WalletFlowsResponse,
} from "./ops-types";

export const opsKeys = {
  dashboard: ["ops", "dashboard"] as const,
  findings: (params: FindingsListParams) =>
    ["ops", "findings", "list", params] as const,
  finding: (id: number) => ["ops", "findings", id] as const,
  runs: (params: RunsListParams) => ["ops", "runs", "list", params] as const,
  run: (id: number) => ["ops", "runs", id] as const,
  walletFlows: (walletID: string, field: string, breakFlowID: number) =>
    ["ops", "wallets", walletID, field, breakFlowID] as const,
  tradeIntegrity: (tradeID: string) =>
    ["ops", "trades", tradeID] as const,
  brokerPositions: (params: BrokerPositionsParams) =>
    ["ops", "brokers", "positions", params] as const,
  brokerConservation: (brokerID: string, currency?: string) =>
    ["ops", "brokers", brokerID, "conservation", currency ?? ""] as const,
  providerTrace: (q: ProviderTraceQuery) => ["ops", "provider", "trace", q] as const,
};

export async function fetchDashboard(): Promise<DashboardSummary> {
  return api<DashboardSummary>("/admin/ops/dashboard");
}

export interface FindingsListParams {
  task_type?: TaskType[];
  code?: string[];
  severity?: Severity[];
  entity_type?: string;
  only_open?: boolean;
  from_unix_ms?: number;
  until_unix_ms?: number;
  run_id?: number;
  limit?: number;
  cursor?: string;
  // SM-782 / SM-721 / SM-725 broker dims.
  currency?: string[];
  broker_id?: string[];
  root_broker_id?: string[];
  subtree_broker_id?: string;
  broker_account_id?: string[];
  operation_type?: string[];
  lifecycle_state?: string[];
  // SM-665 case filters; owner "none" = unassigned.
  case_state?: CaseState[];
  owner?: string;
}

export async function fetchFindings(p: FindingsListParams): Promise<FindingsPage> {
  const params: Record<string, string | number | undefined> = {
    task_type: p.task_type?.join(","),
    code: p.code?.join(","),
    severity: p.severity?.join(","),
    entity_type: p.entity_type,
    only_open: p.only_open ? "true" : undefined,
    from_unix_ms: p.from_unix_ms,
    until_unix_ms: p.until_unix_ms,
    run_id: p.run_id,
    limit: p.limit ?? 50,
    cursor: p.cursor,
    currency: p.currency?.join(","),
    broker_id: p.broker_id?.join(","),
    root_broker_id: p.root_broker_id?.join(","),
    subtree_broker_id: p.subtree_broker_id,
    broker_account_id: p.broker_account_id?.join(","),
    operation_type: p.operation_type?.join(","),
    lifecycle_state: p.lifecycle_state?.join(","),
    case_state: p.case_state?.join(","),
    owner: p.owner,
  };
  return api<FindingsPage>(`/admin/ops/findings${qs(params)}`);
}

export async function fetchFinding(id: number): Promise<Finding> {
  return api<Finding>(`/admin/ops/findings/${id}`);
}

export async function resolveFinding(id: number, notes: string): Promise<{ resolved: number }> {
  return api(`/admin/ops/findings/${id}/resolve`, {
    method: "POST",
    body: { notes },
  });
}

export async function reopenFinding(id: number): Promise<{ reopened: number }> {
  return api(`/admin/ops/findings/${id}/reopen`, {
    method: "POST",
    body: {},
  });
}

export async function bulkResolveFindings(
  finding_ids: number[],
  notes: string,
): Promise<{ resolved_count: number }> {
  return api(`/admin/ops/findings/bulk-resolve`, {
    method: "POST",
    body: { finding_ids, notes },
  });
}

export interface RunsListParams {
  task_def_id?: number[];
  task_type?: TaskType[];
  status?: string[];
  trigger_kind?: string[];
  from_unix_ms?: number;
  until_unix_ms?: number;
  limit?: number;
  cursor?: string;
}

export async function fetchRuns(p: RunsListParams): Promise<RunsPage> {
  const params: Record<string, string | number | undefined> = {
    task_def_id: p.task_def_id?.join(","),
    task_type: p.task_type?.join(","),
    status: p.status?.join(","),
    trigger_kind: p.trigger_kind?.join(","),
    from_unix_ms: p.from_unix_ms,
    until_unix_ms: p.until_unix_ms,
    limit: p.limit ?? 50,
    cursor: p.cursor,
  };
  return api<RunsPage>(`/admin/ops/runs${qs(params)}`);
}

export async function fetchRun(id: number): Promise<RunDetail> {
  return api<RunDetail>(`/admin/ops/runs/${id}`);
}

export async function fetchWalletFlows(
  walletID: string,
  field: string,
  breakFlowID: number,
  windowBefore = 10,
  windowAfter = 10,
): Promise<WalletFlowsResponse> {
  const params = {
    field,
    break_flow_id: breakFlowID,
    window_before: windowBefore,
    window_after: windowAfter,
  };
  return api<WalletFlowsResponse>(
    `/admin/ops/wallets/${encodeURIComponent(walletID)}/flows${qs(params)}`,
  );
}

export async function fetchTradeIntegrity(tradeID: string): Promise<TradeIntegrity> {
  return api<TradeIntegrity>(
    `/admin/ops/trades/${encodeURIComponent(tradeID)}/integrity`,
  );
}

// --- SM-725 broker integrity ------------------------------------------

export interface BrokerPositionsParams {
  root_broker_id?: string;
  currency?: string;
  lifecycle_state?: string;
  subtree_broker_id?: string;
}

/** The broker-recon sweep's per node × currency snapshot. */
export async function fetchBrokerPositions(p: BrokerPositionsParams): Promise<BrokerPositions> {
  return api<BrokerPositions>(`/admin/ops/aggregates/broker${qs({ ...p })}`);
}

/** One node and its subtree, per currency, with conservation terms. */
export async function fetchBrokerConservation(brokerID: string, currency?: string): Promise<BrokerPositions> {
  return api<BrokerPositions>(
    `/admin/ops/brokers/${encodeURIComponent(brokerID)}/conservation${qs({ currency })}`,
  );
}

// --- SM-665 finding case lifecycle + provider trace (phase 15) ---------

/** Assign an open finding; "me" assigns the caller, "" unassigns. */
export async function assignFinding(id: number, owner_admin_id: string): Promise<{ owner_admin_id: string }> {
  return api(`/admin/ops/findings/${id}/assign`, { method: "POST", body: { owner_admin_id } });
}

/** Move an open finding's case; approved_action is an action NAME, never an amount. */
export async function setFindingState(
  id: number,
  state: CaseState,
  approved_action: string,
): Promise<{ case_state: CaseState }> {
  return api(`/admin/ops/findings/${id}/state`, { method: "POST", body: { state, approved_action } });
}

export async function fetchProviderTrace(q: ProviderTraceQuery): Promise<ProviderTrace> {
  return api<ProviderTrace>(`/admin/ops/provider/trace${qs({ ...q })}`);
}
